/** @format */

import axios from "axios";
import {
	IConfirmPasswordResetPayload,
	IPasswordResetPayload,
	ISearchUserSendAddRequestPayload,
	IUserPayload,
} from "../../utility/types/userType";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import { IVerifyAndConfirmAccountPayload } from "../../components/verifyAndConfirmAccount/types";
import { ISignInPayload } from "../../components/signIn/types";

export const signUpUser = async (userData: IUserPayload) => {
	try {
		const response = await axios.post(`${API_BASE_URL}/user`, userData);

		if (!response.data) {
			throw new Error(`Error creating user`);
		}

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const confirmAndVerifyUserAccount = async (
	userVerifyData: IVerifyAndConfirmAccountPayload,
) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/user/verify`,
			userVerifyData,
			{ withCredentials: true },
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const resendVerificationCode = async (email: string) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/user/resend-verification-code`,
			{ email },
			{ withCredentials: true },
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const fetchUserVerification = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/verification/user/${userId}`,
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const signInUserRequest = async (signInPayload: ISignInPayload) => {
	try {
		const payload = {
			email: signInPayload.username,
			password: signInPayload.password,
		};
		const response = await axios.post(`${API_BASE_URL}/user/login`, payload, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const submitGetUser = async (userId: string) => {
	try {
		const response = await axios.get(`${API_BASE_URL}/user/${userId}`, {
			withCredentials: true,
		});

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const submitGetMultipleUsersById = async (userIds: string[]) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/user/get-users/${userIds.join(",")}`,
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {}
};

export const verifySession = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/user/verify-session`, {
			withCredentials: true,
		});
		return response.data; // Should return session validity status
	} catch (error) {
		console.error("Session verification failed", error);
		return { isValid: false };
	}
};

export const fetchToken = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/token`);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const logOutUser = async () => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/user/log-out`,
			{},
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const updateAccount = async (formData: FormData, userId: string) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/user/${userId}`,
			formData,
			{
				withCredentials: true,
				headers: {
					"Content-Type": "multipart/form-data",
				},
			},
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const accountPasswordReset = async (email: IPasswordResetPayload) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/user/initiate-password-reset`,
			email,
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const confirmAccountPasswordReset = async (
	userData: IConfirmPasswordResetPayload,
) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/user/confirm-password-reset`,
			userData,
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const getPresignedImageUrl = async (objectKey: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/user/presigned-url/${objectKey}`,
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const submitSearchUserToSendAddRequest = async (
	searchValues: ISearchUserSendAddRequestPayload,
) => {
	try {
		const queryParams = new URLSearchParams(searchValues as any).toString();

		const response = await axios.get(
			`${API_BASE_URL}/search-user?${queryParams}`,
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const submitFetchAllCirclesOfMember = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/user/${userId}/my-circles`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const submitFetchUserRating = async (userId: string) => {
	try {
		const response = await axios.get(`${API_BASE_URL}/rating/user/${userId}`, {
			withCredentials: true,
		});

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const submitFetchAllJoinRequestsOfUser = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/user/${userId}/all-join-requests`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const fetchAccountVerificationByUserId = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/verification/user/${userId}`,
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const fetchUserStats = async (userId: string) => {
	try {
		const response = await axios.get(`${API_BASE_URL}/user/${userId}/stats`, {
			withCredentials: true,
		});

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const fetchGetAllUsersAsAdmin = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/admin/users/all`, {
			withCredentials: true,
		});

		return response.data.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const banUserAsAdmin = async (userId: string, reason: string) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/admin/users/${userId}/ban`,
			{ reason },
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const unbanUserAsAdmin = async (userId: string) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/admin/users/${userId}/unban`,
			{},
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const deleteUserAsAdmin = async (userId: string, reason: string) => {
	try {
		const response = await axios.delete(
			`${API_BASE_URL}/admin/users/${userId}`,
			{
				data: { reason },
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

export const restoreUserAsAdmin = async (userId: string) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/admin/users/${userId}/restore`,
			{},
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

// ==================== Admin-specific User Management APIs ====================

/**
 * Get a single user by userId (admin only)
 */
export const submitGetUserByIdAsAdmin = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/get-user/${userId}`,
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Update a user's information (admin only)
 */
export const submitUpdateUserByIdAsAdmin = async (
	userId: string,
	updateData: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		profileImage?: string;
	},
) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/admin/update-user/${userId}`,
			updateData,
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Mark user onboarding as complete
 */
export const markOnboardingComplete = async (userId: string) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/user/${userId}`,
			{ hasSeenOnboarding: true },
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Permanently delete a user (admin only)
 */
export const submitDeleteUserByIdAsAdmin = async (
	userId: string,
	reason?: string,
) => {
	try {
		const response = await axios.delete(
			`${API_BASE_URL}/admin/remove-user/${userId}`,
			{
				data: { reason },
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};
